// src/components/LetterResult.jsx
import { useRef, useState, useEffect } from "react";
import { PAPERS, ANIMAL_EMOJIS, SUPPORT_URL } from "../utils/constants";
import { downloadLetterAsPNG } from "../utils/downloadPNG";
import { playTypeClick } from "../utils/typeSound";

const PAPER_BG = {
  lp1: "linear-gradient(160deg, #fff5f8, #fff9f0)",
  lp2: "linear-gradient(160deg, #f0f8f5, #eaf5fc)",
  lp3: "linear-gradient(160deg, #faf6ee, #f5ede0)",
  lp4: "linear-gradient(160deg, #f8f0fe, #fef0f8)",
  lp5: "linear-gradient(160deg, #eef4fc, #eef9f2)",
  lp6: "linear-gradient(160deg, #2d2a3e, #3d3560)",
};

const PAPER_ACCENT = {
  lp1: "linear-gradient(90deg, #f9b4c0, #ffd6a5)",
  lp2: "linear-gradient(90deg, #a8d8ea, #c9e8d4)",
  lp3: "linear-gradient(90deg, #d4b896, #c8a878)",
  lp4: "linear-gradient(90deg, #d4a8e8, #f4a8d4)",
  lp5: "linear-gradient(90deg, #a8c8e8, #a8d8b8)",
  lp6: "linear-gradient(90deg, #6060c0, #9060c0)",
};

const isDark = (lp) => lp === "lp6";

export default function LetterResult({
  letter,
  userName,
  animalName,
  animalType,
  paperId,
  onGoHome,
  onEditEpisodes,
  saveData,
}) {
  const letterRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [consent, setConsent] = useState(null);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);

  // 타이핑 효과
  const [displayedText, setDisplayedText] = useState("");
  const [typingDone, setTypingDone] = useState(false);

  useEffect(() => {
    if (!letter) return;
    let idx = 0;
    setDisplayedText("");
    setTypingDone(false);

    const timer = setInterval(() => {
      const chunk = letter.slice(idx, idx + 2);
      idx += 2;
      setDisplayedText(letter.slice(0, idx));
      if (chunk.trim()) playTypeClick();
      if (idx >= letter.length) {
        clearInterval(timer);
        setTypingDone(true);
      }
    }, 35);

    return () => clearInterval(timer);
  }, [letter]);

  const skipTyping = () => {
    setDisplayedText(letter);
    setTypingDone(true);
  };

  const handleConsent = (agreed) => {
    setConsent(agreed ? "agreed" : "declined");
    if (agreed && saveData) {
      fetch("/api/save-letter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...saveData, letterText: letter }),
      }).catch(() => {});
    }
  };

  const selectedPaper = PAPERS.find((p) => p.id === paperId) || PAPERS[0];
  const lp = selectedPaper.lp;
  const dark = isDark(lp);
  const textColor = dark ? "#d4ccee" : "#5c3d2e";
  const subColor = dark ? "#9088b0" : "#a08060";

  const handleSave = async () => {
    setSaving(true);
    try {
      await downloadLetterAsPNG(letterRef, animalName);
    } catch {
      alert("저장 중 문제가 생겼어요. 잠시 후 다시 시도해주세요.");
    }
    setSaving(false);
  };

  const handleShare = async () => {
    const text = `${letter}\n\n—\n${animalName || "동물 친구"}의 편지, 펫말에서 받아보기\nhttps://www.petmal.kr`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "펫말", text });
        return;
      } catch { /* 사용자 취소 */ }
    } else {
      await navigator.clipboard.writeText(text);
      alert("편지와 링크를 복사했어요. 소중한 분에게 전해주세요 🐾");
    }
  };

  const handleGoHome = () => {
    setShowLeaveWarning(true);
  };

  const confirmGoHome = () => {
    // localStorage 초기화
    localStorage.removeItem("petmal_draft");
    onGoHome();
  };

  return (
    <div style={{ animation: "fadeUp .5s ease" }}>
      {/* 나가기 경고 모달 */}
      {showLeaveWarning && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }}>
          <div style={{
            background: "white", borderRadius: 24, padding: "32px 28px",
            maxWidth: 360, width: "100%",
            boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
            animation: "fadeUp .3s ease",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 36, marginBottom: 16 }}>🐾</div>
            <div style={{
              fontFamily: "'Jua', sans-serif", fontSize: 18, fontWeight: 700, color: "#5c3d2e",
              marginBottom: 12,
            }}>
              정말 나가시겠어요?
            </div>
            <div style={{
              fontSize: 16, fontWeight: 400, color: "#b09070", lineHeight: 1.8, marginBottom: 24,
            }}>
              이대로 나가면 이 편지를<br />다시 볼 수 없어요.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowLeaveWarning(false)}
                style={{
                  flex: 1, padding: 14, borderRadius: 14,
                  background: "white", color: "#b09070", fontSize: 16, fontWeight: 700,
                  fontFamily: "'Jua', sans-serif",
                  border: "1.5px solid #ead6c0", cursor: "pointer",
                }}
              >
                머무르기
              </button>
              <button
                onClick={confirmGoHome}
                style={{
                  flex: 1, padding: 14, borderRadius: 14,
                  background: "linear-gradient(135deg, #c4845a, #d4a070)",
                  color: "white", fontSize: 16, fontWeight: 700,
                  fontFamily: "'Jua', sans-serif",
                  border: "none", cursor: "pointer",
                }}
              >
                나가기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 캡처 대상 영역 */}
      <div
        ref={letterRef}
        style={{
          borderRadius: 24,
          overflow: "hidden",
          marginBottom: 20,
          boxShadow: "0 8px 36px rgba(92,61,46,.12)",
        }}
      >
        <div
          style={{
            background: PAPER_BG[lp],
            padding: "52px 44px 44px",
            minHeight: 520,
            position: "relative",
            fontFamily: "'Gowun Batang', serif",
          }}
        >
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 5,
            background: PAPER_ACCENT[lp],
          }} />

          <div style={{
            fontFamily: "'Jua', sans-serif", fontSize: 11,
            letterSpacing: 3, color: subColor,
            marginBottom: 28, opacity: 0.6,
          }}>
            petmal.kr
          </div>

          <div style={{ fontSize: 20, marginBottom: 16, opacity: 0.4, letterSpacing: 6 }}>
            {selectedPaper.e} {selectedPaper.e} {selectedPaper.e}
          </div>

          <div style={{ fontSize: 13, color: subColor, marginBottom: 28 }}>
            To. 우리 {userName || "주인"}에게
          </div>

          <div
            onClick={!typingDone ? skipTyping : undefined}
            style={{
              fontSize: 16, fontWeight: 400, lineHeight: 2.3, color: textColor,
              whiteSpace: "pre-line",
              cursor: !typingDone ? "pointer" : "default",
              minHeight: 200,
            }}
          >
            {displayedText}
            {!typingDone && (
              <span style={{ animation: "pulse 1s infinite", opacity: 0.6 }}>|</span>
            )}
          </div>

          {!typingDone && (
            <div style={{ fontSize: 12, color: subColor, textAlign: "center", marginTop: 12, opacity: 0.6 }}>
              (터치하면 전체 보기)
            </div>
          )}

          <div style={{ textAlign: "right", marginTop: 36, fontSize: 14, color: subColor }}>
            From. {animalName || "나"} 올림 {ANIMAL_EMOJIS[animalType] || "🐾"}
          </div>

          <div style={{
            position: "absolute", bottom: 20, right: 24,
            fontSize: 40, opacity: 0.06,
          }}>
            {ANIMAL_EMOJIS[animalType] || "🐾"}
          </div>
        </div>
      </div>

      {/* 버튼 영역 — 타이핑 완료 후 표시 */}
      {typingDone && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                flex: 1, display: "flex", alignItems: "center",
                justifyContent: "center", gap: 8, padding: 16,
                borderRadius: 16,
                background: saving ? "#a08060" : "#5c3d2e",
                color: "white", fontSize: 16, fontWeight: 700,
                fontFamily: "'Jua', sans-serif",
                border: "none",
                cursor: saving ? "not-allowed" : "pointer",
                transition: "all .2s",
              }}
            >
              {saving ? "저장 중..." : "PNG 저장"}
            </button>
            <button
              onClick={handleShare}
              style={{
                flex: 1, display: "flex", alignItems: "center",
                justifyContent: "center", gap: 8, padding: 16,
                borderRadius: 16,
                background: "white", color: "#5c3d2e",
                fontSize: 16, fontWeight: 700, fontFamily: "'Jua', sans-serif",
                border: "1.5px solid #ead6c0", cursor: "pointer",
                transition: "all .2s",
              }}
            >
              공유하기
            </button>
          </div>

          {saving && (
            <div style={{ fontSize: 13, color: "#c4845a", textAlign: "center", marginBottom: 10, fontFamily: "'Jua', sans-serif" }}>
              편지를 그림으로 담고 있어요... 🐾
            </div>
          )}

          {/* 후원(커피 사주기) — SUPPORT_URL 설정 시에만 노출 */}
          {SUPPORT_URL && (
            <a
              href={SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 8, padding: 16, marginBottom: 10,
                borderRadius: 16, textDecoration: "none",
                background: "linear-gradient(135deg, #f5e6d3, #fdf0dd)",
                color: "#8a6240", fontSize: 15, fontWeight: 700,
                fontFamily: "'Jua', sans-serif",
                border: "1.5px solid #ead6c0",
                transition: "all .2s",
              }}
            >
              ☕ 편지가 따뜻했다면, 커피 한 잔 사주기
            </a>
          )}

          {/* 데이터 저장 동의 */}
          {consent === null && (
            <div style={{
              background: "#fdf9f5", border: "1.5px solid #ead6c0",
              borderRadius: 16, padding: "20px 24px", marginBottom: 12,
            }}>
              <div style={{ fontSize: 16, fontWeight: 400, color: "#5c3d2e", lineHeight: 1.8, marginBottom: 14 }}>
                이 편지를 더 나은 서비스를 위해 소중히 보관해도 될까요?
                <br />
                <a href="/privacy" style={{ fontSize: 13, color: "#c4845a" }}>개인정보처리방침 보기</a>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => handleConsent(true)}
                  style={{
                    flex: 1, padding: 12, borderRadius: 12,
                    background: "linear-gradient(135deg, #c4845a, #d4a070)",
                    color: "white", fontSize: 16, fontWeight: 700, fontFamily: "'Jua', sans-serif",
                    border: "none", cursor: "pointer",
                  }}
                >
                  동의합니다
                </button>
                <button
                  onClick={() => handleConsent(false)}
                  style={{
                    flex: 1, padding: 12, borderRadius: 12,
                    background: "white", color: "#b09070", fontSize: 16, fontWeight: 700,
                    fontFamily: "'Jua', sans-serif",
                    border: "1.5px solid #ead6c0", cursor: "pointer",
                  }}
                >
                  괜찮아요
                </button>
              </div>
            </div>
          )}

          {consent && (
            <div style={{ fontSize: 13, color: "#a8b89a", textAlign: "center", marginBottom: 12 }}>
              {consent === "agreed" ? "감사합니다! 소중히 보관할게요 🐾" : ""}
            </div>
          )}

          {/* 에피소드 수정하기 */}
          <button
            onClick={onEditEpisodes}
            style={{
              width: "100%", padding: 14, borderRadius: 16,
              background: "white", color: "#5c3d2e",
              border: "1.5px solid #ead6c0", cursor: "pointer",
              fontSize: 16, fontWeight: 700, fontFamily: "'Jua', sans-serif",
              transition: "all .2s", marginBottom: 8,
            }}
          >
            에피소드 내용 수정하기
          </button>

          {/* 처음으로 */}
          <button
            onClick={handleGoHome}
            style={{
              width: "100%", padding: 14, borderRadius: 16,
              background: "none", color: "#b09070",
              border: "1.5px solid #ead6c0", cursor: "pointer",
              fontSize: 16, fontWeight: 700, fontFamily: "'Jua', sans-serif",
              transition: "all .2s",
            }}
          >
            처음으로 돌아가기
          </button>

          <div style={{
            fontSize: 12, color: "#c8b0a0", textAlign: "center",
            marginTop: 10, lineHeight: 1.7,
          }}>
            이대로 나가면 편지를 다시 볼 수 없어요.<br />
            꼭 저장하거나 공유해주세요!
          </div>
        </>
      )}
    </div>
  );
}
